import { useNavigate } from "react-router";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type PageOptions, usePage } from "@/api/pagination";
import ProductsService from "@/api/services/products";
import { ExistingProduct, Product, UpdateProductPayload } from "@/types";

// Not sorted: see useModels.
export function useProducts(params: PageOptions = {}) {
  return usePage<ExistingProduct>(["products", "list"], ProductsService.getProducts, params);
}

export function useAllProducts() {
  return useQuery({
    queryKey: ["products", "all"],
    queryFn: () => ProductsService.getAllProducts(),
    // maintain previous behavior: sort by product name
    select: (data) => data.sort((a, b) => a.product_name.localeCompare(b.product_name)),
  });
}

export function useProduct(productId: ExistingProduct["id"] | undefined) {
  return useQuery({
    queryKey: ["products", "detail", productId],
    queryFn: productId ? () => ProductsService.getProduct(productId) : skipToken,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { product: Product; redirectPath?: string }) => ProductsService.postProduct(payload.product),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: UpdateProductPayload & { redirectPath?: string }) =>
      ProductsService.patchProduct(payload.id, payload.fields!),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({
        queryKey: ["products", variables.id],
      });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { id: string; redirectPath?: string }) => ProductsService.deleteProduct(payload.id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}
